/**
 * Auth Page
 *
 * Login and registration forms for Polygraf Online
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { SIGN_UP_HONEYPOT_FIELD } from '@polygraf/shared'
import { ArrowLeft, CheckCircle, Lock, Mail, MailCheck, User } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { z } from 'zod'
import { BrandMark, BrandWordmark } from '../components/brand-mark'
import { LegalLinks } from '../components/legal-links'
import { type SsoProvider, SsoProviders } from '../components/sso-providers'
import { Alert, Button, buttonClasses, cn, TextField } from '../components/ui'
import { usePageTitle } from '../hooks/use-page-title'
import { useSsoConfig } from '../hooks/use-sso-config'
import { authClient, signIn, signUp, useSession } from '../lib/auth-client'

function getSafeReturnTo(): string {
	// MP-169: discard any returnTo set by AuthGuard when the URL was put there
	// by a logout-in-flight (not by a user trying to access a protected route
	// directly). `app.tsx`'s handleSignOut sets this flag before tearing the
	// session down; consume + clear here so the next login goes to '/' instead
	// of the previous user's page.
	try {
		if (sessionStorage.getItem('polygraf:just-signed-out') === '1') {
			sessionStorage.removeItem('polygraf:just-signed-out')
			return '/'
		}
	} catch {
		// sessionStorage unavailable — fall through to normal returnTo handling.
	}
	const raw = new URLSearchParams(window.location.search).get('returnTo') || '/'
	// Same-origin only. Reject anything that doesn't start with a single `/`,
	// then resolve against `window.location.origin` and reject if the result
	// would point off-origin. This catches `//evil.com`, `/\evil.com`,
	// `/\/evil.com`, embedded schemes, and any other URL parser quirk.
	if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) {
		return '/'
	}
	try {
		const parsed = new URL(raw, window.location.origin)
		if (parsed.origin !== window.location.origin) return '/'
		return parsed.pathname + parsed.search + parsed.hash
	} catch {
		return '/'
	}
}

// Schema creation functions that accept translated messages
const createLoginSchema = (t: (key: string) => string) =>
	z.object({
		email: z.string().email(t('auth.validation.invalidEmail')),
		password: z.string().min(8, t('auth.validation.passwordMin')),
	})

const createRegisterSchema = (t: (key: string) => string) =>
	z
		.object({
			name: z.string().min(2, t('auth.validation.nameMin')),
			email: z.string().email(t('auth.validation.invalidEmail')),
			password: z.string().min(8, t('auth.validation.passwordMin')),
			confirmPassword: z.string(),
			[SIGN_UP_HONEYPOT_FIELD]: z.string().optional(),
		})
		.refine((data) => data.password === data.confirmPassword, {
			message: t('auth.validation.passwordMismatch'),
			path: ['confirmPassword'],
		})

/** Where the verification e-mail's link lands (the server sets the same URL). */
const VERIFIED_CALLBACK_PATH = '/auth?verified=1'

/** Better Auth's error envelope as returned by the client calls. */
type AuthClientError = { code?: string; message?: string; status?: number }

/** An e-mail/password account waiting for its address to be confirmed. */
type VerificationPending = { email: string; reason: 'signup' | 'login' }

type ResendState = 'idle' | 'sending' | 'sent' | 'rateLimited' | 'failed'

type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>
type RegisterFormData = z.infer<ReturnType<typeof createRegisterSchema>>

export default function AuthPage() {
	const { t } = useTranslation()
	const [mode, setMode] = useState<'login' | 'register'>('login')
	usePageTitle(mode === 'login' ? t('common.login') : t('common.register'))
	// Moves focus to the page title when the user toggles between login/register
	// so screen readers announce that the form changed.
	const titleRef = useRef<HTMLHeadingElement>(null)
	const prevMode = useRef(mode)
	const [isLoading, setIsLoading] = useState(false)
	// One provider at a time: the click redirects away, so a second one could
	// never finish anyway. Holding the provider name instead of six booleans
	// keeps "is anything running" a single check.
	const [ssoLoading, setSsoLoading] = useState<SsoProvider | null>(null)
	const [error, setError] = useState<string | null>(null)
	// Set after a sign-up that needs its e-mail confirmed, or a sign-in refused
	// with EMAIL_NOT_VERIFIED: the card then shows "check your inbox".
	const [verificationPending, setVerificationPending] = useState<VerificationPending | null>(null)
	const [resendState, setResendState] = useState<ResendState>('idle')
	const pendingHeadingRef = useRef<HTMLHeadingElement>(null)
	// Landed here from the verification e-mail's link (`/auth?verified=1`). On
	// failure Better Auth appends `&error=…` instead, handled with the other
	// URL errors below. Read once: the params are stripped right after.
	const [justVerified] = useState(() => {
		const params = new URLSearchParams(window.location.search)
		return params.get('verified') === '1' && !params.get('error')
	})
	// The link signs the user in (autoSignInAfterVerification), so a session is
	// normally there already; without one (link opened again later, or in
	// another browser) the page asks them to sign in instead.
	const session = useSession()
	const verifiedAndSignedIn = justVerified && !!session.data?.session
	// Every SSO button is gated on the matching `providers.<name>` flag from
	// /api/auth/config so a click can never land on a provider the server
	// hasn't been configured for (would surface as PROVIDER_NOT_FOUND).
	// Today on Triton: googleEnabled / microsoftEnabled / muniEnabled /
	// eduidEnabled are all true; appleEnabled is false (APPLE_CLIENT_ID
	// is empty pending the MUNI Apple Team Service ID migration) and
	// seznamEnabled is false (SEZNAM_OIDC_* pending OAuth registration).
	const {
		googleEnabled,
		microsoftEnabled,
		appleEnabled,
		muniEnabled,
		eduidEnabled,
		seznamEnabled,
	} = useSsoConfig()

	// Check for SSO callback errors in URL, e.g. after a failed provider sign-in
	// the user lands back on `/auth?error=invalid_client&error_description=AADSTS650051...`.
	// Classify the raw provider error into an actionable message instead of the
	// generic "try again" (retrying a wrong-account error never helps).
	useEffect(() => {
		const params = new URLSearchParams(window.location.search)
		const ssoError = params.get('error')
		const ssoDesc = params.get('error_description')
		if (!ssoError && !ssoDesc) return
		// Keep the raw provider error in the console for debugging; users see the
		// friendly message below.
		console.error('[auth] SSO callback error:', ssoError, ssoDesc)
		const blob = `${ssoError ?? ''} ${ssoDesc ?? ''}`
		let key = 'auth.errors.ssoFailed'
		if (params.get('verified') === '1') {
			// The verification link failed (Better Auth: token_expired,
			// invalid_token, user_not_found). Signing in sends a fresh link.
			key =
				ssoError === 'token_expired'
					? 'auth.verification.linkExpired'
					: 'auth.verification.linkInvalid'
		} else if (ssoError === 'access_denied') {
			// User dismissed the consent / sign-in prompt.
			key = 'auth.errors.ssoCancelled'
		} else if (ssoError === 'account_not_linked') {
			// An account already exists on this e-mail, created by a different
			// sign-in method. The server refuses to link the two implicitly
			// (GHSA-g38m-r43w-p2q7 — see `accountLinkingOptions` in the server's
			// auth-providers.ts). Retrying the same provider always reproduces
			// it, so tell the user to use the method they registered with.
			key = 'auth.errors.ssoAccountNotLinked'
		} else if (/AADSTS(650051|50020|50194)/.test(blob) || /unmanaged/i.test(blob)) {
			// The account is from an organization/tenant the app isn't provisioned
			// for — most often a personal or unmanaged Microsoft tenant rather than
			// the university. Point the user at their MUNI account; "try again"
			// would just reproduce it.
			key = 'auth.errors.ssoWrongOrg'
		}
		setError(t(key))
		// Strip the error params so a refresh or back-navigation doesn't re-surface
		// a stale provider error.
		window.history.replaceState({}, '', window.location.pathname)
	}, [t])

	// Drop `?verified=1` once read so a refresh does not repeat the message.
	useEffect(() => {
		if (justVerified) window.history.replaceState({}, '', window.location.pathname)
	}, [justVerified])

	// Move focus to the "check your inbox" heading when it replaces the form, so
	// a screen reader announces the new state.
	useEffect(() => {
		if (verificationPending) pendingHeadingRef.current?.focus()
	}, [verificationPending])

	// On mode switch (login <-> register), move focus to the heading so the new
	// title is announced. Comparing against the previous mode avoids stealing
	// focus on the initial mount.
	useEffect(() => {
		if (prevMode.current !== mode) {
			prevMode.current = mode
			titleRef.current?.focus()
		}
	}, [mode])

	// Better Auth's `signIn.social` returns `{ data, error }` instead of throwing
	// on API errors like PROVIDER_NOT_FOUND — translate the most common ones into
	// user-friendly messages, fall back to the server message, then to a generic
	// catch-all. Network/throw errors fall into the surrounding try/catch.
	const messageForSsoError = (
		err: { code?: string; message?: string } | null | undefined,
	): string => {
		if (!err) return t('auth.errors.ssoFailed')
		if (err.code === 'PROVIDER_NOT_FOUND') return t('auth.errors.providerNotConfigured')
		return err.message ?? t('auth.errors.ssoFailed')
	}

	const runSsoLogin = async (provider: SsoProvider) => {
		setSsoLoading(provider)
		setError(null)
		try {
			const result = await authClient.signIn.social({
				provider,
				callbackURL: `${window.location.origin}${getSafeReturnTo()}`,
				errorCallbackURL: `${window.location.origin}/auth`,
			})
			// On success Better Auth redirects, so we only reach here on errors.
			if (result?.error) {
				console.error(`[auth] ${provider} SSO error:`, result.error)
				setError(messageForSsoError(result.error))
				setSsoLoading(null)
			}
		} catch (err) {
			console.error(`[auth] ${provider} SSO redirect error:`, err)
			setError(t('auth.errors.ssoFailed'))
			setSsoLoading(null)
		}
	}

	// Localize the Better Auth errors a user can act on; anything else keeps the
	// server's message, then the generic fallback.
	const messageForEmailAuthError = (err: AuthClientError, fallbackKey: string): string => {
		if (err.status === 429) return t('auth.errors.tooManyAttempts')
		switch (err.code) {
			case 'INVALID_EMAIL_OR_PASSWORD':
				return t('auth.errors.invalidCredentials')
			case 'USER_ALREADY_EXISTS':
			case 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL':
				return t('auth.errors.emailExists')
			// The honeypot's refusal is vague on purpose; so is the message.
			case 'SIGN_UP_REJECTED':
				return t(fallbackKey)
			default:
				return err.message || t(fallbackKey)
		}
	}

	const showVerificationPending = (email: string, reason: VerificationPending['reason']) => {
		setResendState('idle')
		setError(null)
		setVerificationPending({ email, reason })
	}

	const resendVerificationEmail = async () => {
		if (!verificationPending) return
		setResendState('sending')
		try {
			const result = await authClient.sendVerificationEmail({
				email: verificationPending.email,
				callbackURL: `${window.location.origin}${VERIFIED_CALLBACK_PATH}`,
			})
			if (result.error) {
				console.error('[auth] resend verification failed:', result.error)
				setResendState(result.error.status === 429 ? 'rateLimited' : 'failed')
				return
			}
			setResendState('sent')
		} catch (err) {
			console.error('[auth] resend verification failed:', err)
			setResendState('failed')
		}
	}

	const loginForm = useForm<LoginFormData>({
		resolver: zodResolver(createLoginSchema(t)),
	})
	const { setFocus: setLoginFocus } = loginForm

	const registerForm = useForm<RegisterFormData>({
		resolver: zodResolver(createRegisterSchema(t)),
	})
	const { setFocus: setRegisterFocus } = registerForm

	const handleLogin = async (data: LoginFormData) => {
		setIsLoading(true)
		setError(null)
		try {
			const result = await signIn.email({ email: data.email, password: data.password })
			if (result.error) {
				if (result.error.code === 'EMAIL_NOT_VERIFIED') {
					// The server has just sent a fresh link (sendOnSignIn).
					showVerificationPending(data.email, 'login')
				} else {
					setError(messageForEmailAuthError(result.error, 'auth.errors.loginFailed'))
				}
				setIsLoading(false)
				return
			}
			// Use a full-document navigation instead of react-router `navigate()`.
			// The destination's session guard (e.g. /auth/desktop) runs its
			// `useEffect` before Better Auth's `useSession()` store has refetched
			// after sign-in, so `navigate()` lets the guard see `session=null` for
			// one tick and bounce back to /auth. A hard reload makes the new page
			// mount with the cookie already in place and `useSession()`
			// initialised from the server.
			window.location.assign(getSafeReturnTo())
			// Defensive: if the navigation never actually leaves the page (CSP
			// block, service-worker intercept, etc.), don't strand the user with
			// a permanent spinner. Component unmount on real navigation makes
			// this a no-op.
			setIsLoading(false)
		} catch (err) {
			console.error('[auth] login failed:', err)
			setError(t('auth.errors.loginFailedRetry'))
			setIsLoading(false)
		}
	}

	const handleRegister = async (data: RegisterFormData) => {
		setIsLoading(true)
		setError(null)
		try {
			const body = {
				email: data.email,
				password: data.password,
				name: data.name,
				callbackURL: `${window.location.origin}${VERIFIED_CALLBACK_PATH}`,
				[SIGN_UP_HONEYPOT_FIELD]: data[SIGN_UP_HONEYPOT_FIELD] ?? '',
			}
			const result = await signUp.email(body)
			if (result.error) {
				setError(messageForEmailAuthError(result.error, 'auth.errors.registerFailed'))
				setIsLoading(false)
				return
			}
			// E-mail verification on (the default): the account exists but has
			// no session until the link in the e-mail is opened.
			if (!result.data?.token) {
				showVerificationPending(data.email, 'signup')
				registerForm.reset()
				setIsLoading(false)
				return
			}
			// Verification off in this env: signed in already.
			// See handleLogin — full reload avoids the useSession() store race.
			window.location.assign(getSafeReturnTo())
			setIsLoading(false)
		} catch (err) {
			console.error('[auth] register failed:', err)
			setError(t('auth.errors.registerFailedRetry'))
			setIsLoading(false)
		}
	}

	return (
		<div className="min-h-screen bg-base-200 flex flex-col">
			{/* Header */}
			<header className="bg-base-100 border-b border-base-300 py-4">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex items-center gap-4">
						<Link
							to="/"
							className={cn(buttonClasses({ variant: 'ghost', size: 'sm' }), 'p-2 rounded-xl')}
							aria-label={t('common.back')}
						>
							<ArrowLeft size={20} />
						</Link>
						<Link to="/" className="flex items-center gap-2">
							<BrandMark />
							<BrandWordmark />
						</Link>
					</div>
				</div>
			</header>

			{/* Main Content */}
			<main className="flex-1 flex items-center justify-center p-4">
				<div className="card-refined w-full max-w-md p-8">
					{verifiedAndSignedIn ? (
						// biome-ignore lint/a11y/useSemanticElements: status live region, not an <output>
						<div className="text-center" role="status">
							<div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
								<CheckCircle size={32} className="text-success" />
							</div>
							<h1 className="text-2xl font-display mb-2">{t('auth.verification.verifiedTitle')}</h1>
							<p className="text-base-content/60 mb-6">{t('auth.verification.verifiedSignedIn')}</p>
							<Button variant="primary" block onClick={() => window.location.assign('/')}>
								{t('auth.verification.continue')}
							</Button>
						</div>
					) : verificationPending ? (
						<div className="text-center">
							<div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
								<MailCheck size={32} className="text-primary" />
							</div>
							<h1
								ref={pendingHeadingRef}
								tabIndex={-1}
								className="text-2xl font-display mb-2 outline-none"
							>
								{verificationPending.reason === 'signup'
									? t('auth.verification.checkInboxTitle')
									: t('auth.verification.notVerifiedTitle')}
							</h1>
							<p className="text-base-content/70 mb-2">
								{verificationPending.reason === 'signup'
									? t('auth.verification.checkInboxBody', { email: verificationPending.email })
									: t('auth.verification.notVerifiedBody', { email: verificationPending.email })}
							</p>
							<p className="text-sm text-base-content/60 mb-6">
								{t('auth.verification.noEmailHint')}
							</p>
							<div aria-live="polite" aria-atomic="true">
								{resendState === 'sent' ? (
									<Alert tone="success" className="mb-4 text-left">
										{t('auth.verification.resent')}
									</Alert>
								) : resendState === 'rateLimited' ? (
									<Alert tone="warning" className="mb-4 text-left">
										{t('auth.errors.tooManyAttempts')}
									</Alert>
								) : resendState === 'failed' ? (
									<Alert tone="error" className="mb-4 text-left">
										{t('auth.verification.resendFailed')}
									</Alert>
								) : null}
							</div>
							<Button
								variant="ghost"
								block
								loading={resendState === 'sending'}
								onClick={() => void resendVerificationEmail()}
							>
								{resendState === 'sending'
									? t('auth.verification.resending')
									: t('auth.verification.resend')}
							</Button>
							<button
								type="button"
								onClick={() => {
									setVerificationPending(null)
									setMode('login')
								}}
								className="mt-4 inline-flex items-center gap-2 text-sm text-primary hover:underline"
							>
								<ArrowLeft size={16} />
								{t('auth.backToLogin')}
							</button>
						</div>
					) : (
						<>
							{/* Title */}
							<div className="text-center mb-8">
								<h1
									ref={titleRef}
									tabIndex={-1}
									className="text-2xl font-display mb-2 outline-none"
								>
									{mode === 'login' ? t('auth.loginTitle') : t('auth.registerTitle')}
								</h1>
								<p className="text-base-content/60">
									{mode === 'login' ? t('auth.loginDescription') : t('auth.registerDescription')}
								</p>
							</div>

							{/* Error Message — persistent live region so screen readers reliably
						announce errors whenever the text changes (role=alert mounted
						after the fact is announced unreliably by NVDA/JAWS). */}
							<div role="alert" aria-live="assertive" aria-atomic="true">
								{error ? (
									<Alert tone="error" className="mb-6">
										{error}
									</Alert>
								) : null}
							</div>

							{/* Verified via the e-mail link but not signed in here (link
						opened again, or in another browser). */}
							{justVerified && !session.isPending && !verifiedAndSignedIn ? (
								<Alert tone="success" className="mb-6" title={t('auth.verification.verifiedTitle')}>
									{t('auth.verification.verifiedSignIn')}
								</Alert>
							) : null}

							{/* Identity providers. Each is gated on the matching /api/auth/config
						flag so a click can never land on a provider the server was not
						configured for (would surface as PROVIDER_NOT_FOUND, e.g. Apple while
						APPLE_CLIENT_ID is empty pending the MUNI Apple Team migration). */}
							<SsoProviders
								enabled={{
									muni: muniEnabled,
									eduid: eduidEnabled,
									google: googleEnabled,
									microsoft: microsoftEnabled,
									apple: appleEnabled,
									seznam: seznamEnabled,
								}}
								loading={ssoLoading}
								busy={isLoading}
								onSelect={runSsoLogin}
							/>

							{/* Login Form */}
							{mode === 'login' && (
								<form
									onSubmit={loginForm.handleSubmit(handleLogin, (errors) => {
										const first = Object.keys(errors)[0] as keyof LoginFormData | undefined
										if (first) setLoginFocus(first)
									})}
									className="space-y-4"
								>
									<TextField
										id="login-email"
										type="email"
										label={t('auth.email')}
										placeholder={t('auth.emailPlaceholder')}
										leadingIcon={<Mail size={18} />}
										autoComplete="email"
										required
										error={loginForm.formState.errors.email?.message}
										{...loginForm.register('email')}
									/>
									<TextField
										id="login-password"
										type="password"
										label={t('auth.password')}
										placeholder={t('auth.passwordPlaceholder')}
										leadingIcon={<Lock size={18} />}
										withPasswordToggle
										showPasswordLabel={t('auth.showPassword')}
										hidePasswordLabel={t('auth.hidePassword')}
										autoComplete="current-password"
										required
										error={loginForm.formState.errors.password?.message}
										{...loginForm.register('password')}
									/>

									<div className="flex justify-end">
										<Link
											to="/auth/forgot-password"
											className="text-sm text-primary hover:underline"
										>
											{t('auth.forgotPassword')}
										</Link>
									</div>

									<Button type="submit" loading={isLoading} variant="primary" block>
										{isLoading ? t('auth.loggingIn') : t('auth.loginButton')}
									</Button>
								</form>
							)}

							{/* Register Form */}
							{mode === 'register' && (
								<form
									onSubmit={registerForm.handleSubmit(handleRegister, (errors) => {
										const first = Object.keys(errors)[0] as keyof RegisterFormData | undefined
										if (first) setRegisterFocus(first)
									})}
									className="space-y-4"
								>
									<TextField
										id="register-name"
										type="text"
										label={t('auth.name')}
										placeholder={t('auth.namePlaceholder')}
										leadingIcon={<User size={18} />}
										autoComplete="name"
										required
										error={registerForm.formState.errors.name?.message}
										{...registerForm.register('name')}
									/>
									<TextField
										id="register-email"
										type="email"
										label={t('auth.email')}
										placeholder={t('auth.emailPlaceholder')}
										leadingIcon={<Mail size={18} />}
										autoComplete="email"
										required
										error={registerForm.formState.errors.email?.message}
										{...registerForm.register('email')}
									/>
									<TextField
										id="register-password"
										type="password"
										label={t('auth.password')}
										placeholder={t('auth.passwordPlaceholder')}
										leadingIcon={<Lock size={18} />}
										withPasswordToggle
										showPasswordLabel={t('auth.showPassword')}
										hidePasswordLabel={t('auth.hidePassword')}
										autoComplete="new-password"
										required
										error={registerForm.formState.errors.password?.message}
										{...registerForm.register('password')}
									/>
									<TextField
										id="register-confirm-password"
										type="password"
										label={t('auth.confirmPassword')}
										placeholder={t('auth.passwordPlaceholder')}
										leadingIcon={<Lock size={18} />}
										withPasswordToggle
										showPasswordLabel={t('auth.showPassword')}
										hidePasswordLabel={t('auth.hidePassword')}
										autoComplete="new-password"
										required
										error={registerForm.formState.errors.confirmPassword?.message}
										{...registerForm.register('confirmPassword')}
									/>
									{/* Honeypot: off-screen, out of the tab order and hidden from
								assistive tech, so only a bot filling every input reaches it.
								Not `display:none` — some bots skip fields hidden that way. */}
									<div
										aria-hidden="true"
										className="absolute -left-[10000px] top-auto w-px h-px overflow-hidden"
									>
										<label htmlFor="register-website">{t('auth.honeypotLabel')}</label>
										<input
											id="register-website"
											type="text"
											tabIndex={-1}
											autoComplete="off"
											{...registerForm.register(SIGN_UP_HONEYPOT_FIELD)}
										/>
									</div>

									<Button type="submit" loading={isLoading} variant="primary" block>
										{isLoading ? t('auth.registering') : t('auth.createAccount')}
									</Button>
								</form>
							)}

							{/* Mode Switch */}
							<div className="mt-6 text-center">
								{mode === 'login' ? (
									<p className="text-base-content/60">
										{t('auth.noAccount')}{' '}
										<button
											type="button"
											onClick={() => {
												setMode('register')
												setError(null)
											}}
											className="text-primary hover:underline font-medium"
										>
											{t('auth.registerLink')}
										</button>
									</p>
								) : (
									<p className="text-base-content/60">
										{t('auth.hasAccount')}{' '}
										<button
											type="button"
											onClick={() => {
												setMode('login')
												setError(null)
											}}
											className="text-primary hover:underline font-medium"
										>
											{t('auth.loginLink')}
										</button>
									</p>
								)}
							</div>

							{/* Continue as guest — PUBLIC spaces are browsable without an
					    account (the directory at `/` is open), so anyone who reached
					    this page without meaning to sign in can return to browsing. */}
							<div className="mt-4 text-center">
								<Link to="/" className="text-sm text-base-content/60 hover:underline">
									{t('auth.continueAsGuest')}
								</Link>
							</div>
						</>
					)}
				</div>
			</main>

			{/* Footer */}
			<footer className="bg-base-100 border-t border-base-300 py-4">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6">
					<p className="text-sm text-base-content/60">{t('auth.copyright')}</p>
					<LegalLinks />
				</div>
			</footer>
		</div>
	)
}
